import { FormAggregatedResult } from "@/components/form-result/aggregated-result/FormAggregatedResult.tsx";
import { PrintDocument, PrintHeader } from "@/components/print/index.ts";
import {
  useSurveyAggregatedResults,
  useSurveyDetails,
} from "@/queries/surveys/use-surveys.query.ts";
import { getApiSurveyStatusTitle } from "@/types/survey.types.ts";
import { Box, FullScreenPage, MetricTile, Title } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

export function SurveySummaryPrintPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();

  const surveyId = id ? parseInt(id) : undefined;
  const showMore = new URLSearchParams(location.search).get("show-more") === "true";

  const { data: survey } = useSurveyDetails(surveyId, !!surveyId);
  const { data: surveyResults } = useSurveyAggregatedResults(surveyId, {});

  if (!survey) {
    return <FullScreenPage />;
  }

  const stats = survey.participants_stats;
  const responseRate =
    stats.total_count > 0
      ? ((stats.completed_count / stats.total_count) * 100).toFixed() + "%"
      : "-";

  return (
    <FullScreenPage contentSize="lg" isCenterVertical={true}>
      <Box className="py-6 print:py-0">
        <PrintDocument
          header={<PrintHeader title={survey.title} />}
          footerLabel={t("core:print_generated_with")}
        >
          <Box className="grid grid-cols-3 gap-3 py-(--gap-lg)">
            <MetricTile title={t("common:status")} value={getApiSurveyStatusTitle(survey.status)} />
            <MetricTile title={t("common:total")} value={stats.total_count} />
            <MetricTile
              title={t("surveys:participant_status_completed")}
              value={stats.completed_count}
            />
            <MetricTile
              title={t("surveys:participants_waiting")}
              value={stats.invited_count + stats.in_progress_count}
            />
            <MetricTile title={t("common:response_rate")} value={responseRate} />
          </Box>

          {stats.completed_count > 0 && (
            <Box className="ml-20 pt-8">
              <Title
                header={t("surveys:responses")}
                caption={t("surveys:responses_description", {
                  people_answered: t("plurals:people_answered", {
                    count: stats.completed_count,
                  }),
                })}
                paddingBottom
              />
            </Box>
          )}

          {surveyResults && (
            <FormAggregatedResult
              form={surveyResults.form}
              results={surveyResults.results}
              show_more={showMore}
              disableMaxWidth={true}
            />
          )}
        </PrintDocument>
      </Box>
    </FullScreenPage>
  );
}
